import { NavLink, useLocation } from 'react-router-dom';
import { useState } from 'react';
import type { GameKbArtifactStatus, Phase, PhaseStatus } from '@sr-test-wizard/shared';
import { isStakeEngineStudio } from '@sr-test-wizard/shared';
import { BrandMark } from '../components/BrandMark';
import { StatusIcon, type IconKind } from '../components/StatusIcon';
import { SubStepList, type SubStep } from '../components/SubStepList';
import type { RunSummary } from '../state/runStore';
import { useAutoWorkflowStore } from '../state/autoWorkflowStore';
import { AutoWorkflowDialog } from '../features/auto-workflow/AutoWorkflowDialog';

/**
 * Phase ids used by the rail and the `/runs/:id/phase/:n` routes. Visible
 * order is 0..7: Preconditions -> 0, TEXEC data -> 1, EnvLinks -> 2, PullSource
 * -> 3, Images -> 4, Gameplay Video -> 5, Game Knowledge -> 6, TEXEC execution
 * -> 7. Bug review and Stake Engine stay as extras.
 *
 * URL <-> folder mapping is documented in `App.tsx`; the folder names
 * under `features/phaseN` are intentionally NOT renamed so git history
 * stays readable. The rail just wires the visible numbers to the
 * existing panels.
 */
export interface PhaseDefinition {
  id: '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7';
  label: string;
  caption: string;
  status: IconKind;
  locked: boolean;
  subSteps?: SubStep[];
}

/**
 * Compact summary of the env-links bundle so the rail can render the env
 * links phase status without pulling the full bundle. `null` means we
 * haven't loaded it yet (or there are zero jurisdictions and nothing
 * meaningful to show).
 */
export interface EnvLinksReady {
  total: number;
  ready: number;
  recipeStatus: 'ok' | 'missing' | 'invalid';
}

/**
 * Compact summary of the bug review bundle for the rail caption. `null`
 * means we haven't loaded it yet.
 */
export interface BugReviewReady {
  total: number;
  open: number;
}

/**
 * Compact summary of the precondition bundle for the rail caption. `null`
 * means we haven't loaded it yet or no pull has happened. `tpKey` is null
 * when no Testing Preconditions task was found under the SR.
 */
export interface PreconditionsReady {
  tpKey: string | null;
  total: number;
  done: number;
  staged: number;
}

/**
 * Compact summary of the Gameplay Video list for the workflow phase.
 * `null` means we haven't loaded the list for this run yet. We carry both
 * extracted/analysed counts so the sub-step list (Extract / Analyze) can
 * render meaningful progress.
 */
export interface VideosReady {
  total: number;
  extracted: number;
  analysed: number;
  extracting: boolean;
  analyzing: boolean;
}

export interface GameKbReady {
  description: GameKbArtifactStatus;
  report: GameKbArtifactStatus;
}

interface Props {
  run: RunSummary;
  unanalyzed: number;
  totalTests: number;
  imagesCount: number;
  imagesAnalysisExists: boolean;
  criticalFilesLoaded: boolean;
  /** Manifest entries that did not resolve on disk. */
  criticalFilesMissing: number;
  texecLoaded: boolean;
  envLinksReady: EnvLinksReady | null;
  bugReviewReady: BugReviewReady | null;
  preconditionsReady: PreconditionsReady | null;
  videosReady: VideosReady | null;
  gameKbReady?: GameKbReady | null;
  /** True while at least one AI test analysis job is actively running. */
  testsAnalyzing?: boolean;
}

/**
 * Computes the rail's per-phase status from the persisted phase map plus a
 * few derived signals (whether tests are analysed, images are downloaded,
 * etc).
 *
 * Visible ordering:
 *   0. Preconditions                 - Jira Smart Checklist mirror (TP-*)
 *   1. Populate TEXEC Data           - read-only landing (auto-loaded)
 *   2. Generate Environment Links    - depends on TEXEC
 *   3. Pull Source Code              - depends on TEXEC
 *   4. Download & Analyze Images     - depends on Source Code
 *   5. Gameplay Video                - frame extract + analyze
 *   6. Game Knowledge                - description + full report
 *   7. TEXEC execution               - depends on Source Code + Images
 *
 * Bug review / Stake Engine are rendered separately under EXTRAS and are
 * not counted toward overall workflow progress.
 */
export function buildPhaseDefinitions({
  run,
  unanalyzed,
  totalTests,
  imagesCount,
  imagesAnalysisExists,
  criticalFilesLoaded,
  criticalFilesMissing = 0,
  texecLoaded,
  envLinksReady,
  preconditionsReady,
  videosReady,
  gameKbReady = null,
  testsAnalyzing = false,
}: Props): PhaseDefinition[] {
  const phaseStatus = (p: Phase): PhaseStatus => run.phases?.[p] ?? 'pending';
  const pull = phaseStatus('pull-source');
  const drive = phaseStatus('drive');
  const analyzeImages = phaseStatus('analyze-images');
  const loadTests = phaseStatus('load-tests');

  // Sub-step lists are tagged by visible step number now (the underlying
  // panel folders / phase keys are unchanged).
  const sourceCodeSubs: SubStep[] = [
    {
      id: 'pull-source',
      label: 'Pull source archive',
      status: pull,
    },
    {
      id: 'critical',
      label: 'Critical files loaded',
      status: !criticalFilesLoaded
        ? 'pending'
        : criticalFilesMissing > 0
          ? 'failed'
          : 'completed',
    },
  ];

  const imagesSubs: SubStep[] = [
    {
      id: 'drive',
      label: 'Download images',
      status: imagesCount > 0 ? 'completed' : drive === 'in_progress' ? 'in_progress' : drive,
    },
    {
      id: 'analyze-images',
      label: 'Analyze images',
      status: imagesAnalysisExists
        ? 'completed'
        : analyzeImages === 'in_progress'
          ? 'in_progress'
          : imagesCount > 0
            ? 'paused'
            : 'pending',
    },
  ];

  const analyzeTestsStatus: IconKind =
    totalTests > 0 && unanalyzed === 0
      ? 'completed'
      : testsAnalyzing
        ? 'in_progress'
        : totalTests > 0
          ? 'paused'
          : 'pending';

  const compareSubs: SubStep[] = [
    {
      id: 'load-tests',
      label: 'Load Xray tests',
      status: loadTests,
    },
    {
      id: 'analyze-tests',
      label: 'AI per-test analysis',
      status: analyzeTestsStatus,
    },
  ];

  const imagesStatus: IconKind =
    imagesCount > 0 && imagesAnalysisExists
      ? 'completed'
      : drive === 'in_progress' || analyzeImages === 'in_progress'
        ? 'in_progress'
        : drive === 'failed' || analyzeImages === 'failed'
          ? 'failed'
          : imagesCount > 0 || drive === 'completed'
            ? 'paused'
            : 'pending';

  const texecStatus: IconKind =
    totalTests > 0 && unanalyzed === 0
      ? 'completed'
      : testsAnalyzing || loadTests === 'in_progress'
        ? 'in_progress'
        : totalTests > 0
          ? 'paused'
          : 'pending';

  return [
    {
      id: '0',
      label: 'Preconditions',
      caption: preconditionsCaption(preconditionsReady),
      status: preconditionsStatus(preconditionsReady),
      locked: false,
    },
    {
      id: '1',
      label: 'Populate TEXEC Data',
      caption: texecLoaded ? 'Loaded from Jira' : 'Read-only',
      status: texecLoaded ? 'completed' : 'pending',
      locked: false,
    },
    {
      id: '2',
      label: 'Generate Environment Links',
      caption: envLinksCaption(envLinksReady),
      status: envLinksStatus(envLinksReady),
      locked: false,
    },
    {
      id: '3',
      label: 'Pull Source Code',
      caption:
        pull === 'completed' ? 'Source extracted' : pull === 'in_progress' ? 'In Progress' : 'Pending',
      status: pull,
      locked: false,
      subSteps: sourceCodeSubs,
    },
    {
      id: '4',
      label: 'Download & Analyze Images',
      caption:
        imagesCount > 0 && imagesAnalysisExists
          ? `${imagesCount} images analysed`
          : imagesCount > 0
            ? `${imagesCount} images`
            : drive === 'in_progress'
              ? 'Downloading...'
              : 'Pending',
      status: imagesStatus,
      locked: false,
      subSteps: imagesSubs,
    },
    {
      id: '5',
      label: 'Gameplay Video',
      caption: videosCaption(videosReady),
      status: videosStatus(videosReady),
      locked: false,
      subSteps: videoSubSteps(videosReady),
    },
    {
      id: '6',
      label: 'Game Knowledge',
      caption: gameKbCaption(gameKbReady),
      status: gameKbStatus(gameKbReady),
      locked: false,
      subSteps: gameKbSubSteps(gameKbReady),
    },
    {
      id: '7',
      label: 'TEXEC execution',
      caption:
        loadTests === 'completed'
          ? totalTests > 0 && unanalyzed === 0
            ? `${totalTests} tests reviewed`
            : `${totalTests} tests`
          : 'Pending',
      status: texecStatus,
      locked: false,
      subSteps: compareSubs,
    },
  ];
}

function preconditionsCaption(ready: PreconditionsReady | null): string {
  if (!ready) return 'Pending';
  if (!ready.tpKey) return 'No TP task found';
  if (ready.total === 0) return 'No items';
  const base = `${ready.done}/${ready.total} done`;
  return ready.staged > 0 ? `${base} · ${ready.staged} staged` : base;
}

function preconditionsStatus(ready: PreconditionsReady | null): IconKind {
  if (!ready) return 'pending';
  if (!ready.tpKey) return 'pending';
  if (ready.total === 0) return 'pending';
  if (ready.done === ready.total) return 'completed';
  if (ready.staged > 0 || ready.done > 0) return 'paused';
  return 'pending';
}

/**
 * Bug review is shown in a separate "EXTRAS" group below the numbered
 * workflow. It still surfaces the open/total caption because that count is
 * useful, but it's intentionally rendered without a step number and
 * excluded from the overall-progress percentage in `PhasesRail`.
 */
function bugReviewCaption(ready: BugReviewReady | null): string {
  if (!ready) return 'Pending';
  if (ready.total === 0) return 'No bugs linked';
  if (ready.open === 0) return `All ${ready.total} bugs done`;
  return `${ready.open} open / ${ready.total} bugs`;
}

function bugReviewIcon(ready: BugReviewReady | null): IconKind {
  if (!ready) return 'pending';
  if (ready.total === 0) return 'pending';
  if (ready.open === 0) return 'completed';
  return 'paused';
}

function envLinksCaption(ready: EnvLinksReady | null): string {
  if (!ready) return 'Pending';
  if (ready.recipeStatus === 'invalid') return 'Recipe invalid';
  if (ready.recipeStatus === 'missing') {
    return ready.total > 0 ? `No recipe (${ready.total} markets)` : 'No recipe yet';
  }
  if (ready.total === 0) return 'No jurisdictions';
  if (ready.ready === ready.total) return `${ready.ready}/${ready.total} markets ready`;
  return `${ready.ready}/${ready.total} markets ready`;
}

function envLinksStatus(ready: EnvLinksReady | null): IconKind {
  if (!ready) return 'pending';
  if (ready.recipeStatus === 'invalid') return 'failed';
  if (ready.recipeStatus === 'missing') return 'pending';
  if (ready.total === 0) return 'pending';
  if (ready.ready === ready.total) return 'completed';
  if (ready.ready > 0) return 'paused';
  return 'pending';
}

/**
 * Overall Gameplay Video status for the rail entry. Green only when at
 * least one video is fully processed (extracted + analysed) AND no work
 * is partially done; yellow while any job is running or partially done;
 * pending when the list is empty.
 */
function videosStatus(ready: VideosReady | null): IconKind {
  if (!ready || ready.total === 0) return 'pending';
  if (ready.extracting || ready.analyzing) return 'in_progress';
  if (ready.extracted === ready.total && ready.analysed === ready.total) {
    return 'completed';
  }
  return 'paused';
}

function videosCaption(ready: VideosReady | null): string {
  if (!ready || ready.total === 0) return 'Frame-by-frame analysis';
  if (ready.analyzing) return 'Analyzing frames...';
  if (ready.extracting) return 'Extracting frames...';
  if (ready.extracted === ready.total && ready.analysed === ready.total) {
    return ready.total === 1 ? '1 video analysed' : `${ready.total} videos analysed`;
  }
  return `${ready.analysed}/${ready.total} videos analysed`;
}

function videoSubSteps(ready: VideosReady | null): SubStep[] {
  if (!ready || ready.total === 0) {
    return [
      { id: 'video-extract', label: 'Extract frames', status: 'pending' },
      { id: 'video-analyze', label: 'Analyze frames', status: 'pending' },
    ];
  }
  const extractStatus: IconKind = ready.extracting
    ? 'in_progress'
    : ready.extracted === ready.total
      ? 'completed'
      : ready.extracted > 0
        ? 'paused'
        : 'pending';
  const analyzeStatus: IconKind = ready.analyzing
    ? 'in_progress'
    : ready.analysed === ready.total
      ? 'completed'
      : ready.analysed > 0 || ready.extracted === ready.total
        ? 'paused'
        : 'pending';
  return [
    { id: 'video-extract', label: 'Extract frames', status: extractStatus },
    { id: 'video-analyze', label: 'Analyze frames', status: analyzeStatus },
  ];
}

function artifactIcon(status: GameKbArtifactStatus | undefined): IconKind {
  if (status === 'ready') return 'completed';
  if (status === 'running') return 'in_progress';
  if (status === 'error') return 'failed';
  return 'pending';
}

function gameKbStatus(ready: GameKbReady | null): IconKind {
  if (!ready) return 'pending';
  if (ready.description === 'running' || ready.report === 'running') return 'in_progress';
  if (ready.description === 'ready' && ready.report === 'ready') return 'completed';
  if (ready.description === 'error' || ready.report === 'error') return 'failed';
  if (ready.description === 'ready' || ready.report === 'ready') return 'paused';
  return 'pending';
}

function gameKbCaption(ready: GameKbReady | null): string {
  if (!ready) return 'Description & report';
  const desc = ready.description === 'ready';
  const report = ready.report === 'ready';
  if (desc && report) return 'Description & report ready';
  if (ready.description === 'running' || ready.report === 'running') return 'Generating...';
  if (desc) return 'Description ready';
  if (report) return 'Report ready';
  return 'Description & report';
}

function gameKbSubSteps(ready: GameKbReady | null): SubStep[] {
  return [
    { id: 'game-kb-description', label: 'Game Description', status: artifactIcon(ready?.description) },
    { id: 'game-kb-report', label: 'Full Report', status: artifactIcon(ready?.report) },
  ];
}

function autoWorkflowStepToPhase(step: string | null | undefined): PhaseDefinition['id'] | null {
  if (!step) return null;
  if (step.startsWith('populate-texec')) return '1';
  if (step.startsWith('env-links')) return '2';
  if (step.startsWith('pull-source')) return '3';
  if (step.includes('images')) return '4';
  if (step.startsWith('video')) return '5';
  if (step.startsWith('game-kb')) return '6';
  if (step.startsWith('load-tests') || step.startsWith('analyze-tests')) return '7';
  return null;
}

interface RailProps extends Props {
  // No extra rail-specific props; PhasesRail simply renders the list.
}

export function PhasesRail(props: RailProps) {
  const phases = buildPhaseDefinitions(props);
  const { pathname } = useLocation();
  const [autoOpen, setAutoOpen] = useState(false);
  const autoStatus = useAutoWorkflowStore((s) => s.status);
  const autoRunning = autoStatus?.running === true;
  const autoPhase = autoRunning ? autoWorkflowStepToPhase(autoStatus?.step) : null;
  const activeMatch = /\/phase\/([0-7])/.exec(pathname);
  const videosCompatActive = /\/videos(?:\/|$)/.test(pathname);
  const gameKbCompatActive = /\/game-kb(?:\/|$)/.test(pathname);
  const wizardCompatActive = /\/wizard(?:\/|$)/.test(pathname);
  const activeId = activeMatch
    ? activeMatch[1]
    : videosCompatActive
      ? '5'
      : gameKbCompatActive
        ? '6'
        : wizardCompatActive
          ? '7'
          : null;
  const bugsActive = /\/bugs(?:\/|$)/.test(pathname);
  const lookAtThatActive = /\/look-at-that(?:\/|$)/.test(pathname);
  const stakeActive = /\/stake-engine(?:\/|$)/.test(pathname);
  const isStakeRun = isStakeEngineStudio(props.run.studio);
  const completedCount = phases.filter((p) => p.status === 'completed').length;
  const overallProgress = Math.round((completedCount / phases.length) * 100);

  const bugStatus = bugReviewIcon(props.bugReviewReady);
  const bugCaption = bugReviewCaption(props.bugReviewReady);

  const gate =
    !props.preconditionsReady ||
    !props.preconditionsReady.tpKey ||
    props.preconditionsReady.total === 0 ||
    props.preconditionsReady.done >= props.preconditionsReady.total
      ? { ok: true, reason: null as string | null }
      : {
          ok: false,
          reason: `Finish Phase 0 first (${props.preconditionsReady.done}/${props.preconditionsReady.total} preconditions done).`,
        };

  return (
    <aside className="phase-rail">
      <div className="phase-rail-header">
        <BrandMark to="/" />
        <div className="phase-rail-subtitle">Core Review Workflow</div>
      </div>

      <div className="phase-rail-auto">
        <button
          type="button"
          className={`btn phase-rail-auto-btn${autoRunning ? ' is-running' : ''}`}
          onClick={() => setAutoOpen(true)}
        >
          {autoRunning ? 'Auto workflow running…' : 'Auto workflow'}
        </button>
        {autoRunning && autoStatus?.message && (
          <div className="phase-rail-auto-msg">{autoStatus.message}</div>
        )}
      </div>

      <div className="phase-rail-section-title">WORKFLOW PHASES</div>
      <nav className="phase-rail-list">
        {phases.map((phase) => {
          const status = autoPhase === phase.id ? 'in_progress' : phase.status;
          const isActive = phase.id === activeId;
          const className = `phase-item phase-item-${status} ${isActive ? 'active' : ''} ${
            phase.locked ? 'locked' : ''
          }`;
          const inner = (
            <>
              <div className="phase-item-row">
                <StatusIcon status={status} size={20} />
                <div className="phase-item-text">
                  <div className="phase-item-label">
                    {phase.id}. {phase.label}
                  </div>
                  <div className="phase-item-caption">
                    {autoPhase === phase.id && autoStatus?.message
                      ? autoStatus.message
                      : phase.caption}
                  </div>
                </div>
              </div>
              {isActive && phase.subSteps && phase.subSteps.length > 0 && (
                <SubStepList steps={phase.subSteps} />
              )}
            </>
          );
          if (phase.locked) {
            return (
              <div key={phase.id} className={className} aria-disabled>
                {inner}
              </div>
            );
          }
          return (
            <NavLink
              key={phase.id}
              to={`/runs/${props.run.id}/phase/${phase.id}`}
              className={className}
            >
              {inner}
            </NavLink>
          );
        })}
      </nav>

      <div className="phase-rail-section-title phase-rail-section-extras">EXTRAS</div>
      <nav className="phase-rail-list phase-rail-extras-list">
        <NavLink
          to={`/runs/${props.run.id}/bugs`}
          className={`phase-item phase-item-${bugStatus} phase-item-extra ${bugsActive ? 'active' : ''}`}
        >
          <div className="phase-item-row">
            <StatusIcon status={bugStatus} size={20} />
            <div className="phase-item-text">
              <div className="phase-item-label">Bugs</div>
              <div className="phase-item-caption">{bugCaption}</div>
            </div>
          </div>
        </NavLink>
        <NavLink
          to={`/runs/${props.run.id}/look-at-that`}
          className={`phase-item phase-item-pending phase-item-extra ${lookAtThatActive ? 'active' : ''}`}
        >
          <div className="phase-item-row">
            <StatusIcon status="pending" size={20} />
            <div className="phase-item-text">
              <div className="phase-item-label">Look at that</div>
              <div className="phase-item-caption">Live play, login handoff, explore</div>
            </div>
          </div>
        </NavLink>
        {isStakeRun && (
          <NavLink
            to={`/runs/${props.run.id}/stake-engine`}
            className={`phase-item phase-item-pending phase-item-extra ${stakeActive ? 'active' : ''}`}
          >
            <div className="phase-item-row">
              <StatusIcon status="pending" size={20} />
              <div className="phase-item-text">
                <div className="phase-item-label">Stake Engine</div>
                <div className="phase-item-caption">EAS math verification &amp; chat</div>
              </div>
            </div>
          </NavLink>
        )}
      </nav>

      <div className="phase-rail-overview">
        <div className="phase-rail-overview-header">
          <span>Workflow Overview</span>
          <span>{overallProgress}%</span>
        </div>
        <div className="progress-bar">
          <div className="progress-bar-fill" style={{ width: `${overallProgress}%` }} />
        </div>
      </div>

      <div className="phase-rail-footer">
        <NavLink to={`/runs/${props.run.id}/audit`} className="btn btn-ghost phase-rail-audit">
          View Audit Log
        </NavLink>
      </div>

      {autoOpen && (
        <AutoWorkflowDialog
          runId={props.run.id}
          gateOk={gate.ok}
          gateReason={gate.reason}
          onClose={() => setAutoOpen(false)}
        />
      )}
    </aside>
  );
}
